// Links in guest messages open inside WhatsApp's own browser. On iPhone that browser keeps storage apart from
// Safari, so the guest signs in there, loses it, and signs in again in Safari. The link page hands the guest to
// the real browser before a code is sent.

export type InAppBrowser = 'android' | 'ios' | null;

/** Marks a link already handed to Chrome, so a failed hand-off never loops. */
const HANDED_OFF = 'ext';

/**
 * Which in-app browser this page is in, or null in a real browser. Android: a WebView says "; wv)" (WhatsApp's
 * Chrome Custom Tab is real Chrome — nothing to do). iPhone: an app's WKWebView has no "Safari/" token, unlike
 * Safari and Chrome for iOS ("CriOS"). Safari's own in-app view looks like Safari and is not detected.
 */
export function detectInAppBrowser(ua: string = navigator.userAgent): InAppBrowser {
  if (/WhatsApp/i.test(ua)) return /Android/i.test(ua) ? 'android' : 'ios';
  if (/Android/i.test(ua) && /; wv\)/.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua) && !/Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) return 'ios';
  return null;
}

/** Whether this page was already handed to Chrome once (it came back: no Chrome, or the app refused). */
export function wasHandedOff(): boolean {
  return new URLSearchParams(window.location.search).has(HANDED_OFF);
}

/** Chrome intent for this page; if Chrome is missing, Android opens the fallback (this page, marked). */
export function chromeIntentUrl(href: string = window.location.href): string {
  const url = new URL(href);
  const fallback = new URL(href);
  fallback.searchParams.set(HANDED_OFF, '1');
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;package=com.android.chrome;`
    + `S.browser_fallback_url=${encodeURIComponent(fallback.toString())};end`;
}

/** Opens this page in Safari (iOS 17+). Older iOS ignores it; the guest uses the menu instead. */
export function safariUrl(href: string = window.location.href): string {
  return href.replace(/^https?:\/\//, 'x-safari-https://');
}
